import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Clock, LogIn, LogOut, Users, RefreshCw, Loader2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffClock() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [date, setDate] = useState("");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // { employee, action: 'in'|'out' }
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/attendance/today-status`);
      setEmployees(res.data?.employees || []);
      setDate(res.data?.date || "");
    } catch (err) {
      console.error("Error loading today status", err);
      toast.error("Failed to load today's attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const performClockAction = async (employee, action, pin = "") => {
    const endpoint = action === "in"
      ? `${API}/employees/attendance/staff-clock-in`
      : `${API}/employees/attendance/staff-clock-out`;
    setSubmitting(true);
    try {
      const res = await axios.post(endpoint, {
        target_employee_id: employee.id,
        cashier_pin: pin,
      });
      toast.success(res.data?.message || "Done");
      setPinDialogOpen(false);
      setPinValue("");
      setPendingAction(null);
      fetchStatus();
    } catch (err) {
      console.error("Staff clock error", err);
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const requestAction = (employee, action) => {
    if (isAdmin) {
      // Admins are already authenticated — skip the PIN dialog
      performClockAction(employee, action, "");
      return;
    }
    setPendingAction({ employee, action });
    setPinValue("");
    setPinDialogOpen(true);
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    if (!pinValue || !/^\d{4,6}$/.test(pinValue)) {
      toast.error("Enter your 4-6 digit PIN");
      return;
    }
    await performClockAction(pendingAction.employee, pendingAction.action, pinValue);
  };

  const casuals = employees.filter(
    (e) => e.employment_type === "casual" || e.salary_type === "hourly"
  );
  const permanent = employees.filter(
    (e) => !(e.employment_type === "casual" || e.salary_type === "hourly")
  );

  const renderRow = (emp) => {
    const statusBadge =
      emp.status === "in" ? (
        <Badge className="bg-green-600 hover:bg-green-600">Clocked In @ {emp.clock_in}</Badge>
      ) : emp.status === "out" ? (
        <Badge variant="secondary">Out @ {emp.clock_out} (In {emp.clock_in})</Badge>
      ) : (
        <Badge variant="outline" className="text-gray-600">Not in yet</Badge>
      );

    return (
      <div
        key={emp.id}
        className="flex items-center justify-between border rounded-lg p-3 bg-white hover:bg-gray-50"
        data-testid={`staff-row-${emp.id}`}
      >
        <div className="flex-1">
          <div className="font-semibold">
            {emp.first_name} {emp.last_name}
            {emp.employee_number && (
              <span className="text-xs text-gray-500 ml-2">({emp.employee_number})</span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {emp.position} · {emp.employment_type || "—"} · {emp.salary_type}
          </div>
          <div className="mt-1">{statusBadge}</div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={emp.status === "not_in" ? "default" : "outline"}
            className={emp.status === "not_in" ? "bg-green-600 hover:bg-green-700" : ""}
            disabled={emp.status !== "not_in"}
            onClick={() => requestAction(emp, "in")}
            data-testid={`clock-in-btn-${emp.id}`}
          >
            <LogIn className="h-4 w-4 mr-1" />
            Clock In
          </Button>
          <Button
            size="sm"
            variant={emp.status === "in" ? "default" : "outline"}
            className={emp.status === "in" ? "bg-red-600 hover:bg-red-700" : ""}
            disabled={emp.status !== "in"}
            onClick={() => requestAction(emp, "out")}
            data-testid={`clock-out-btn-${emp.id}`}
          >
            <LogOut className="h-4 w-4 mr-1" />
            Clock Out
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6" data-testid="staff-clock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            Staff Clock-In
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tap Clock In / Clock Out, then enter your own PIN to confirm.
            {date && <span className="ml-2 font-medium">· {date}</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                Casual / Hourly ({casuals.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {casuals.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No casual/hourly staff on file.</div>
              ) : (
                casuals.map(renderRow)
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                Permanent / Monthly ({permanent.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {permanent.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No permanent staff on file.</div>
              ) : (
                permanent.map(renderRow)
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="cashier-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Confirm with Your PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {pendingAction && (
              <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2">
                <strong>{pendingAction.action === "in" ? "Clock IN" : "Clock OUT"}:</strong>{" "}
                {pendingAction.employee.first_name} {pendingAction.employee.last_name}
              </div>
            )}
            <div>
              <Label className="mb-1 block">Your PIN (4-6 digits)</Label>
              <Input
                type="password"
                inputMode="numeric"
                autoFocus
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter") confirmAction(); }}
                placeholder="••••"
                data-testid="cashier-pin-input"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setPinDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={confirmAction}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-pin-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
